import React, { useEffect } from "react";
import { ExternalLink, Loader2 } from "lucide-react";
import { useSettings } from "@/lib/settings";
import BookNets from "@/pages/BookNets";

/**
 * Resolves the booking provider at runtime:
 *   - skedda  → window.location.replace(skeddaUrl)         (full redirect)
 *   - iframe  → <iframe src={skeddaUrl}>                   (embedded)
 *   - inhouse → renders the legacy multi-step BookNets flow
 */
const BookGateway = () => {
  const { booking_provider, booking_skedda_url, show_inhouse_booking, loading } = useSettings();

  // Effective provider: if admin selected "inhouse" but the feature flag is off,
  // fall back to Skedda redirect so end-users never hit a dead page.
  const effectiveProvider = (booking_provider === "inhouse" && !show_inhouse_booking)
    ? "skedda"
    : booking_provider;

  useEffect(() => {
    if (!loading && effectiveProvider === "skedda" && booking_skedda_url) {
      window.location.replace(booking_skedda_url);
    }
  }, [effectiveProvider, booking_skedda_url, loading]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-black" data-testid="book-gateway-loading">
        <Loader2 className="w-6 h-6 animate-spin text-spike-blue-light" />
      </div>
    );
  }

  if (effectiveProvider === "skedda") {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-black text-spike-text px-6 text-center" data-testid="book-gateway-skedda-redirect">
        <Loader2 className="w-6 h-6 animate-spin text-spike-blue-light" />
        <p className="mt-4 text-sm text-spike-text-muted font-exo">Redirecting you to our booking system…</p>
        <a
          href={booking_skedda_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-2 text-spike-blue-light hover:underline text-sm"
        >
          Tap here if it doesn't open <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    );
  }

  if (effectiveProvider === "iframe") {
    return (
      <div className="min-h-[90vh] bg-black" data-testid="book-gateway-iframe">
        <div className="bg-amber-500/10 border-b border-amber-500/30 px-6 py-3 text-xs text-amber-300 text-center font-exo">
          Trouble loading the booking page below? <a href={booking_skedda_url} target="_blank" rel="noopener noreferrer" className="underline font-semibold">Open Skedda in a new tab</a>.
        </div>
        <iframe
          src={booking_skedda_url}
          title="Spike Sports — Booking"
          className="w-full h-[88vh] border-0 bg-white"
          allow="payment; clipboard-read; clipboard-write"
        />
      </div>
    );
  }

  // inhouse
  return <BookNets />;
};

export default BookGateway;
